import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLatest } from 'ahooks';
import { MinusIcon, PlusIcon, RotateCcwIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

/**
 * The image viewer in the chat lightbox.
 * {@link ChatLightbox}
 */
export function ImageViewer({
    url,
    alt,
    containerRef,
}: {
    url: string;
    alt: string;
    containerRef: React.RefObject<HTMLDivElement | null>;
}) {
    const imgRef = useRef<HTMLImageElement>(null);
    const [zoom, setZoom] = useState(MIN_ZOOM);
    const zoomRef = useLatest(zoom);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const panRef = useLatest(pan);
    const isZoomed = zoom > MIN_ZOOM;
    const [isDragging, setIsDragging] = useState(false);
    const dragState = useRef({
        active: false,
        didDrag: false,
        startX: 0,
        startY: 0,
        startPanX: 0,
        startPanY: 0,
    });

    const resetZoom = useCallback(() => {
        setZoom(MIN_ZOOM);
        setPan({ x: 0, y: 0 });
    }, []);

    function zoomTo(target: number) {
        const clamped = clampZoom(target);
        setZoom(clamped);
        if (clamped <= MIN_ZOOM) {
            setPan({ x: 0, y: 0 });
        } else {
            setPan(clampPanRef.current(panRef.current, clamped));
        }
    }

    function clampPan(p: { x: number; y: number }, zoom?: number) {
        const container = containerRef.current;
        const imgElement = imgRef.current;
        if (!container || !imgElement) return p;

        zoom = zoom ?? zoomRef.current;
        // The img element's layout size (before CSS transform).
        const imgW = imgElement.offsetWidth;
        const imgH = imgElement.offsetHeight;
        const cW = container.clientWidth;
        const cH = container.clientHeight;
        // Scaled dimensions after zoom.
        const scaledW = imgW * zoom;
        const scaledH = imgH * zoom;
        // Pan far enough to see the image edge + 1/4 of the container.
        const overscroll = 0.25;
        const maxX = Math.max((scaledW - cW) / 2 + cW * overscroll, 0);
        const maxY = Math.max((scaledH - cH) / 2 + cH * overscroll, 0);

        return {
            x: Math.min(Math.max(p.x, -maxX), maxX),
            y: Math.min(Math.max(p.y, -maxY), maxY),
        };
    }

    // Drag to pan (pointer events).
    function handlePointerDown(e: React.PointerEvent) {
        if (!isZoomed) return;
        dragState.current = {
            active: true,
            didDrag: false,
            startX: e.clientX,
            startY: e.clientY,
            startPanX: pan.x,
            startPanY: pan.y,
        };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }

    function handlePointerMove(e: React.PointerEvent) {
        if (!dragState.current.active) return;
        const dx = e.clientX - dragState.current.startX;
        const dy = e.clientY - dragState.current.startY;
        dragState.current.didDrag = true;
        setIsDragging(true);
        setPan(
            clampPan({
                x: dragState.current.startPanX + dx,
                y: dragState.current.startPanY + dy,
            }),
        );
    }

    function handlePointerUp() {
        dragState.current.active = false;
        setIsDragging(false);
    }

    // Use refs to prevent need to add to useEffect dependencies.
    const clampPanRef = useLatest(clampPan);
    const zoomToRef = useLatest(zoomTo);
    const resetZoomRef = useLatest(resetZoom);

    // Wheel zoom (toward cursor).
    useEffect(() => {
        const container = containerRef.current;
        const imgElement = imgRef.current;
        if (!container || !imgElement) return;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            // Use the container rect so cursor offset is relative to a
            // fixed centre point that doesn't shift with zoom/pan.
            const rect = container.getBoundingClientRect();
            const cx = e.clientX - rect.left - rect.width / 2;
            const cy = e.clientY - rect.top - rect.height / 2;

            setZoom((prevZoom) => {
                const nextZoom = clampZoom(prevZoom * (1 - e.deltaY * 0.001));
                if (nextZoom <= MIN_ZOOM) {
                    setPan({ x: 0, y: 0 });
                } else {
                    // Adjust pan so the point under the cursor stays fixed.
                    const ratio = nextZoom / prevZoom;
                    setPan((prevPan) => {
                        return clampPanRef.current(
                            {
                                x: cx - ratio * (cx - prevPan.x),
                                y: cy - ratio * (cy - prevPan.y),
                            },
                            nextZoom,
                        );
                    });
                }
                return nextZoom;
            });
        };

        imgElement.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            imgElement.removeEventListener('wheel', handleWheel);
        };
    }, [containerRef, clampPanRef]);

    // Keyboard shortcuts for zoom and reset.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case '+':
                case '=':
                    e.preventDefault();
                    zoomToRef.current(zoomRef.current + ZOOM_STEP);
                    break;
                case '-':
                    e.preventDefault();
                    zoomToRef.current(zoomRef.current - ZOOM_STEP);
                    break;
                case '0':
                    e.preventDefault();
                    resetZoomRef.current();
                    break;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [zoomRef, zoomToRef, resetZoomRef]);

    return (
        <>
            <img
                ref={imgRef}
                className={cn(
                    'max-h-full max-w-full select-none bg-gray-900 object-contain will-change-transform',
                    isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in',
                    isDragging && 'cursor-grabbing',
                )}
                src={url}
                alt={alt}
                draggable={false}
                style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    if (isZoomed) {
                        // Ignore clicks that were actually drags.
                        if (!dragState.current?.didDrag) {
                            resetZoom();
                        }
                    } else {
                        zoomTo(2);
                    }
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            />
            <ZoomControls
                zoom={zoom}
                onZoomChange={zoomTo}
                onReset={resetZoom}
            />
        </>
    );
}

/**
 * Zoom controls: reset, minus, slider, plus, percentage.
 */
function ZoomControls({
    zoom,
    onZoomChange,
    onReset,
}: {
    zoom: number;
    onZoomChange: (z: number) => void;
    onReset: () => void;
}) {
    const controlButtonClass =
        'flex size-8 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-50 disabled:pointer-events-none';

    return (
        <div
            className="backdrop-blur-xs absolute bottom-4 left-4 z-10 flex items-center gap-1.5 rounded-lg bg-black/60 px-2 py-1.5"
            onClick={(e) => e.stopPropagation()}
        >
            {/* Reset to fit. */}
            <button
                className={controlButtonClass}
                disabled={zoom <= MIN_ZOOM}
                aria-label="Reset zoom"
                onClick={onReset}
            >
                <RotateCcwIcon className="size-4" />
            </button>
            {/* Zoom out. */}
            <button
                className={controlButtonClass}
                disabled={zoom <= MIN_ZOOM}
                aria-label="Zoom out"
                onClick={() => onZoomChange(zoom - ZOOM_STEP)}
            >
                <MinusIcon className="size-4" />
            </button>
            {/* Slider. */}
            <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.1}
                value={zoom}
                onChange={(e) => onZoomChange(Number(e.target.value))}
                aria-label="Zoom level"
                className={cn(
                    'outline-hidden h-1 w-24 cursor-pointer appearance-none rounded-full bg-white/25',
                    // Webkit thumb
                    '[&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white',
                    // Firefox thumb
                    '[&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-white',
                )}
            />
            {/* Zoom in. */}
            <button
                className={controlButtonClass}
                disabled={zoom >= MAX_ZOOM}
                aria-label="Zoom in"
                onClick={() => onZoomChange(zoom + ZOOM_STEP)}
            >
                <PlusIcon className="size-4" />
            </button>
            {/* Percentage label. */}
            <span className="ml-1 min-w-[3ch] text-center text-3.5 tabular-nums leading-tight text-white/80">
                {Math.round(zoom * 100)}%
            </span>
        </div>
    );
}

/**
 * Clamp a zoom value between MIN_ZOOM and MAX_ZOOM.
 */
function clampZoom(value: number) {
    return Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM);
}
